import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerService, FeeWithTax } from '../../../../../../../../../../services/customer/customer.service';
import { dateStringFromISO8601 } from '../../../../../../../../../../utils';
import { CustomerMembership } from '../../../../../../../../../../models/membership';
import { OrganizationConfigService } from '../../../../../../../../../../services/organization-config/organization-config.service';
import { PaymentService } from '../../../../../../../../../../services/payment/payment.service';
import { initPayment } from '../../../../../../../modals/utils';
import { PaymentProcessorType } from '../../../../../../../../../../models/organization-config-enums';
import { MembershipPurchaseType } from '../../../../../../../../../../models/membership-enums';

declare const emergepay: any;

@Component({
  selector: 'app-edit-prepaid-customer-membership-freeze-immediately',
  templateUrl: './edit-prepaid-customer-membership-freeze-immediately.component.html',
  styleUrls: ['./edit-prepaid-customer-membership-freeze-immediately.component.css']
})
export class EditPrepaidCustomerMembershipFreezeImmediatelyComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  public feeWithTax: FeeWithTax = null;
  public hasFee: boolean = null;

  public reason: string = null;

  public loadingFee = false;
  public loadingInvoice = false;
  public loadingPayment = false;
  public loadingSave = false;

  public errorMessage = '';

  public dateStringFromISO8601 = dateStringFromISO8601;
  public MembershipPurchaseType = MembershipPurchaseType;

  private saveClickedSubscription: Subscription;

  constructor(
    public orgConfig: OrganizationConfigService,
    private activeModal: NgbActiveModal,
    private customerService: CustomerService,
    private paymentService: PaymentService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });

    this.loadFee();

    if (this.membership.payment_processor_type === PaymentProcessorType.gravity) {
      emergepay.init();
    }

    // Reload the org config, in case the membership freeze reason policy has changed.
    this.orgConfig.loadOrganizationConfig();
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  private loadFee() {
    this.loadingFee = true;
    this.loadingEvent.emit(true);

    this.customerService
      .getMembershipImmediateFreezeFee(this.membership)
      .then(feeWithTax => {
        this.feeWithTax = feeWithTax;
        this.hasFee = parseFloat(this.feeWithTax.total) > 0;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to freeze this membership.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => {
        this.loadingFee = false;
        this.loadingEvent.emit(this.isLoading());
      });
  }

  private handleSave() {
    if (this.isLoading()) { return; }

    const orgConfig = this.orgConfig.getOrganizationConfig();
    if (orgConfig.require_membership_freeze_reason && (this.reason === null)) {
      this.errorMessage = 'Please select a reason for the freeze.';
      return;
    }

    if (this.hasFee) {
      this.getInvoiceAndPayFreezeFee();
    } else {
      this.freezeMembership();
    }
  }

  private getInvoiceAndPayFreezeFee() {
    /**
     * Create an invoice for the membership's freeze fee, then pay it using the
     * configured payment processor
     *
     * The membership will be frozen when the payment processor confirms the
     * payment via postback.
     */
    this.loadingInvoice = true;
    this.loadingEvent.emit(true);

    this.errorMessage = '';

    this.customerService
      .getMembershipImmediateFreezeInvoice(this.membership, this.reason)
      .then(invoiceId => {
        this.payFreezeFee(invoiceId);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to freeze this membership.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => {
        this.loadingInvoice = false;
        this.loadingEvent.emit(this.isLoading());
      });
  }

  private payFreezeFee(invoiceId: number) {
    this.loadingPayment = true;
    this.loadingEvent.emit(true);

    this.errorMessage = '';

    initPayment(
      this.modalService,
      invoiceId,
      this.membership.payment_processor_type,
      this.membership.owner_id,
      'Capitan membership #' + this.membership.id.toString() + ' freeze fee',
      location.href,
      location.href,
      () => {
        this.savedCallback('Membership frozen successfully.');
        this.activeModal.close();
      }
    );
  }

  private freezeMembership() {
    /**
     * Freeze the membership directly, without paying a fee.
     */
    this.loadingSave = true;
    this.loadingEvent.emit(true);

    this.customerService
      .freezeMembershipImmediately(
        this.membership,
        this.reason
      )
      .then(() => {
        this.savedCallback('Membership frozen successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to freeze this membership.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => {
        this.loadingSave = false;
        this.loadingEvent.emit(this.isLoading());
      });
  }

  private isLoading() {
    return (
      this.loadingFee ||
      this.loadingInvoice ||
      this.loadingPayment ||
      this.loadingSave
    );
  }

}
